import React, { useRef, useEffect, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Autoplay, Pagination, Navigation, EffectFade } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/pagination'
import 'swiper/css/navigation'
import 'swiper/css/effect-fade'
import { getCarousels } from '../services/api'

const HeroCarousel = () => {
  const swiperRef = useRef(null)
  const [carouselItems, setCarouselItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // 默认轮播数据 - 当API失败时使用
  const defaultCarouselItems = [
    {
      id: 1,
      type: 'image',
      src: '/api/placeholder/1920/800',
      alt: '科技解决方案展示',
      title: '',
      description: '',
      link: '/products'
    },
    {
      id: 2,
      type: 'image',
      src: '/api/placeholder/1920/800',
      alt: '产品展示',
      title: '',
      description: '',
      link: '/products'
    },
    {
      id: 3,
      type: 'video',
      src: '/api/placeholder/video.mp4',
      poster: '/api/placeholder/1920/800',
      alt: '公司介绍视频',
      title: '',
      description: '',
      link: '/company'
    }
  ]

  // 从后端API获取轮播图数据
  useEffect(() => {
    const fetchCarousels = async () => {
      try {
        setLoading(true)
        setError(null)
        const data = await getCarousels()
        
        // 过滤只显示启用的轮播图，并按display_order排序
        const activeCarousels = data
          .filter(item => item.is_active !== false)
          .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
        
        if (activeCarousels.length > 0) {
          setCarouselItems(activeCarousels)
        } else {
          // 如果没有启用的轮播图，使用默认数据
          setCarouselItems(defaultCarouselItems)
        }
      } catch (err) {
        console.error('获取轮播图数据失败:', err)
        setError('无法加载轮播图数据')
        // API失败时使用默认数据
        setCarouselItems(defaultCarouselItems)
      } finally {
        setLoading(false)
      }
    }

    fetchCarousels()
  }, [])

  useEffect(() => {
    // 处理鼠标悬停暂停功能
    const handleMouseEnter = () => {
      if (swiperRef.current && swiperRef.current.swiper) {
        swiperRef.current.swiper.autoplay.stop()
      }
    }

    const handleMouseLeave = () => {
      if (swiperRef.current && swiperRef.current.swiper) {
        swiperRef.current.swiper.autoplay.start()
      }
    }

    const swiperElement = swiperRef.current
    if (swiperElement) {
      swiperElement.addEventListener('mouseenter', handleMouseEnter)
      swiperElement.addEventListener('mouseleave', handleMouseLeave)
    }

    return () => {
      if (swiperElement) {
        swiperElement.removeEventListener('mouseenter', handleMouseEnter)
        swiperElement.removeEventListener('mouseleave', handleMouseLeave)
      }
    }
  }, [])

  return (
    <div 
      ref={swiperRef}
      className="relative h-screen max-h-[800px]"
    >
      <Swiper
        spaceBetween={0}
        slidesPerView={1}
        loop={true}
        autoplay={{
          delay: 5000, // 5秒自动播放
          disableOnInteraction: false,
        }}
        pagination={{
          clickable: true,
          dynamicBullets: true,
        }}
        navigation={true}
        effect={'fade'}
        fadeEffect={{
          crossFade: true
        }}
        modules={[Autoplay, Pagination, Navigation, EffectFade]}
        className="h-full"
      >
        {carouselItems.map((item) => (
          <SwiperSlide key={item.id}>
            <div className="relative h-full w-full">
              {item.type === 'image' ? (
                <div className="w-full h-full flex items-center justify-center bg-gray-100">
                  <img
                    src={item.src}
                    alt={item.alt}
                    className="max-w-full max-h-full object-contain"
                    loading="eager"
                    decoding="async"
                    style={{ 
                      width: 'auto', 
                      height: 'auto',
                      imageRendering: 'auto' // 确保浏览器使用最佳渲染算法
                    }}
                    onError={(e) => {
                      // 图片加载失败时使用占位符
                      e.target.src = '/api/placeholder/1920/800';
                    }}
                  />
                </div>
              ) : (
                <div className="video-container">
                  <video
                    className="w-full h-full object-contain"
                    poster={item.poster}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata" // 预加载元数据，优化视频加载
                  >
                    <source src={item.src} type="video/mp4" />
                    <source src={item.src.replace('.mp4', '.webm')} type="video/webm" />
                    您的浏览器不支持视频播放。
                  </video>
                </div>
              )}
              
              {/* 可点击的图片/视频覆盖层 */}
              {item.link && (
                <a 
                  href={item.link} 
                  className="absolute inset-0 block cursor-pointer"
                  aria-label={`跳转到${item.alt || '页面'}`}
                >
                  {/* 透明覆盖层，确保整个区域可点击 */}
                  <div className="absolute inset-0 bg-transparent"></div>
                </a>
              )}
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* 自定义进度条 */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10">
        <div className="flex space-x-2">
          {carouselItems.map((_, index) => (
            <div
              key={index}
              className="w-3 h-3 bg-white bg-opacity-50 rounded-full cursor-pointer swiper-pagination-bullet"
              onClick={() => {
                if (swiperRef.current && swiperRef.current.swiper) {
                  swiperRef.current.swiper.slideTo(index)
                }
              }}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export default HeroCarousel
